<template>
  <a-popover
    v-bind="attrs"
    v-model:open="newOpen"
    :arrow="false"
    trigger="click"
    :placement="placement"
    :overlay-class-name="`${bem.b()}`"
  >
    <a-badge :dot="useBadge && activeState" :offset="[-2, 2]">
      <ll-icon-base-pro
        :class="`${bem.e('icon')} ${bem.is(
          newOpen || activeState ? 'active' : '',
        )}`"
        :show-tip="false"
        :icon-name="searchIcon || 'icon-screen'"
        bg-color="var(--color-fill-tertiary)"
        hover-bg-color="var(--color-fill-secondary)"
        :size="size"
        :label="label"
        :icon-size="16"
      />
    </a-badge>
    <template #content>
      <a-form-item-rest>
        <div :class="bem.e('content')">
          <div
            v-for="(item, idx) in searchList"
            :key="idx"
            :class="bem.e('list-item')"
          >
            <!-- 字段名 -->
            <ll-select
              v-model:value="item.key"
              style="width: 150px"
              placeholder="请选择字段"
              size="small"
              :field-names="{
                label: 'title',
                value: 'key',
                options: 'options',
              }"
              :options="
                localList.map((i) => ({
                  ...i,
                  disabled: searchList.some((y) => y.key === i.key),
                }))
              "
              @select="handleField"
            />
            <!-- 运算符 -->
            <ll-select
              v-model:value="item.operator"
              style="width: 150px"
              :disabled="!operatorSelectable"
              placeholder="请选择运算符"
              :options="
                item.key ? [...ops.base, ...ops.text, ...ops.compare] : []
              "
              size="small"
              @select="(value) => handleOperator(value, item)"
            />

            <!-- 值 -->
            <template
              v-if="
                item.operator !== 'isEmpty' &&
                item.operator !== 'isNotEmpty' &&
                item.operator !== 'isNull' &&
                item.operator !== 'isNotNull' &&
                item.operator !== 'isNullOrEmpty'
              "
            >
              <ll-input
                v-if="!item.key"
                v-model:value="item.value"
                placeholder="请输入"
                :style="{ minWidth: '250px', flex: 1 }"
                size="small"
              />
              <component
                :is="configMap[item.key].type"
                v-else
                v-bind="
                  Object.fromEntries(
                    Object.entries(configMap[item.key].props).filter(
                      ([key]) => !key.toLowerCase().includes('formcreate'),
                    ),
                  )
                "
                v-model:value="item.value"
                :style="{ minWidth: '250px', flex: 1 }"
                size="small"
              />
            </template>
            <div v-else :style="{ minWidth: '250px', flex: 1 }"></div>

            <!-- 移除图标 -->
            <ll-icon
              v-if="searchList.length > 1"
              icon-name="icon-remove"
              :class="bem.e('remove-icon')"
              :size="14"
              @click="handleRemove(idx)"
            />
          </div>
        </div>

        <div :class="bem.e('operate')">
          <div :class="bem.e('operate-btn')">
            <ll-button
              :disabled="disabledAdd"
              type="text"
              size="small"
              :icon-space="8"
              @click="handleAdd"
            >
              <ll-icon icon-name="icon-list-add" />
              {{ addSearchText || '添加筛选条件' }}
            </ll-button>

            <ll-button
              v-if="useLocalStorage && localKey"
              :disabled="!searchList.some((item) => item.key)"
              :class="bem.e('operate-btn')"
              type="text"
              size="small"
              :icon-space="8"
              @click="nameOpen = true"
            >
              <ll-icon icon-name="icon-add" />
              {{ saveSchemeText || '保存方案' }}
            </ll-button>
          </div>

          <!-- 操作区插槽 -->
          <div :class="bem.e('operate-slot')">
            <slot name="operate" />
          </div>
        </div>

        <!-- 本地方案展示区 -->
        <div
          v-if="useLocalStorage && localKey && localScheme.length > 0"
          :class="bem.e('local-scheme')"
        >
          <ll-tag
            v-for="(item, idx) in localScheme"
            :key="idx"
            shape="round"
            @click="useScheme(idx)"
          >
            {{ item.name }}
            <ll-icon icon-name="icon-close2" @click.stop="clearScheme(idx)" />
          </ll-tag>
        </div>
      </a-form-item-rest>
    </template>
  </a-popover>
  <scheme-name
    v-if="nameOpen"
    v-model:open="nameOpen"
    @cancel="handleCancel"
    @ok="handleOk"
  />
</template>

<script lang="ts" setup>
import { createNamespace, getRandomCode } from '@ll-plus/utils'
import { cloneDeep } from 'lodash-es'
import {
  computed,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  useAttrs,
  watch,
} from 'vue'
import { ops } from './config'
import { customSearcherFieldItem, customSearcherProps } from './custom-searcher'

import SchemeName from './scheme-name.vue'

defineOptions({ name: 'LlCustomSearcher' })

const props = defineProps(customSearcherProps)
const emits = defineEmits(['update:open', 'field-select'])

const bem = createNamespace('custom-searcher')

const attrs = useAttrs()

const searchList = reactive([
  { key: null, operator: null, value: undefined },
] as any)

const localList = computed(() => [...props.fieldList])
const storageVersion = ref(0)
const nameOpen = ref(false)

// 本地存储的方案
const localScheme = computed({
  get: () => {
    storageVersion.value // 手动触发响应式
    if (props.useLocalStorage && props.localKey) {
      try {
        const item = localStorage.getItem(props.localKey)
        return item ? JSON.parse(item) : [] // 确保返回数组
      } catch {
        return [] // 解析失败时返回默认值
      }
    } else {
      return []
    }
  },
  set: (value: any) => {
    storageVersion.value // 手动触发响应式
    if (props.useLocalStorage && props.localKey) {
      try {
        return localStorage.setItem(props.localKey, JSON.stringify(value))
      } catch (error) {
        return console.error('保存到本地存储失败:', error)
      }
    }
  },
})

const newOpen = computed({
  get() {
    return nameOpen.value || props.open
  },
  set(o: boolean) {
    if (!nameOpen.value) {
      emits('update:open', o)
    }
  },
})

// 禁用新增
const disabledAdd = computed(() => {
  return (
    localList.value.length === searchList.length ||
    (props.maxSearchCount > 0 && props.maxSearchCount <= searchList.length)
  )
})

// 运算符映射
// const operatorConfigMap = computed(() => {
//   const map = {} // 创建一个空对象来存映射关系

//   // 遍历所有筛选字段(localList)
//   localList.value.forEach((fieldConfig) => {
//     map[fieldConfig.key] = typeMapOperator[fieldConfig.dataType] || []
//   })

//   return map // 返回完整的映射表
// })

// 配置映射
const configMap = computed(() => {
  const map = {} // 创建一个空对象来存映射关系

  // 遍历所有筛选字段(localList)
  localList.value.forEach((fieldConfig) => {
    map[fieldConfig.key] = fieldConfig
  })

  return map as any // 返回完整的映射表
})

// 新增
const handleAdd = () => {
  searchList.push({ key: null, operator: null, value: undefined })
}

const handleCancel = () => {
  setTimeout(() => {
    nameOpen.value = false
  }, 100)
}
// 获取方案名称
const handleOk = (name: string) => {
  setTimeout(() => {
    nameOpen.value = false
  }, 100)
  localScheme.value = [
    ...localScheme.value,
    {
      key: getRandomCode(),
      name: name,
      value: searchList.filter((item) => item.key),
    },
  ]
  storageVersion.value++
}

// 使用方案
const useScheme = (idx: number) => {
  searchList.length = 0
  searchList.push(...cloneDeep(localScheme.value[idx].value))

  filterSearchList()
}

// 移除方案
const clearScheme = (idx: number) => {
  const copyArr = cloneDeep(localScheme.value)

  copyArr.splice(idx, 1)
  localScheme.value = [...copyArr]
  storageVersion.value++
}

// 移除
const handleRemove = (idx: number) => {
  searchList.splice(idx, 1)
}

// 切换字段
const handleField = (value: string, option: customSearcherFieldItem) => {
  const target = searchList.find((item) => item.key === value)

  if (target) {
    if (!props.operatorSelectable) {
      target.operator = option.conditionType || null
    }

    if (option?.props?.value) {
      target.value = option.props.value
    } else {
      target.value = undefined
    }
  }

  emits('field-select', value, option)
}

// 切换运算符
const handleOperator = (value: string, item: any) => {
  if (
    item.operator === 'isEmpty' ||
    item.operator === 'isNotEmpty' ||
    item.operator === 'isNull' ||
    item.operator === 'isNotNull' ||
    item.operator === 'isNullOrEmpty'
  ) {
    item.value = undefined
  }
}

// 获取当前查询值
const getValue = () => {
  return searchList
}

// 清除查询字段
const clearValue = (range: 'all' | 'field', fields?: string[]) => {
  if (range === 'field') {
    if (fields && fields?.length > 0) {
      for (let i = searchList.length - 1; i >= 0; i--) {
        if (fields.includes(searchList[i].key)) {
          searchList.splice(i, 1)
        }
      }
    }
  } else {
    searchList.length = 0
  }

  if (searchList.length === 0) {
    searchList.push({ key: null, operator: null, value: undefined })
  }
}

// 修改查询值
const setValue = (config: Record<string, Record<string, any>>) => {
  Object.entries(config).forEach(([key, updates]) => {
    const targetItem = searchList.find((item: any) => item.key === key)
    if (targetItem && updates) {
      Object.assign(targetItem, updates)
    }
  })
}

// 调整组件配置
const handleConfig = (config: Record<string, Record<string, any>>) => {
  Object.entries(config).forEach(([key, updates]) => {
    const targetObj = localList.value.find((i: any) => i.key === key) as any
    if (targetObj && updates) {
      Object.assign(targetObj.props, updates)
    }
  })
}

// 过滤多余查询条件（不存在字段列表时）
const filterSearchList = () => {
  // 提取 fieldList 中所有有效的 key 值
  const validFields = localList.value.map((item) => item.key).filter(Boolean)

  // 从后往前遍历，避免删除元素时索引错乱
  for (let i = searchList.length - 1; i >= 0; i--) {
    // 如果当前项的 key 不在有效字段列表中，则删除
    if (!validFields.includes(searchList[i].key && searchList[i].key)) {
      searchList.splice(i, 1)
    }
  }
  if (searchList.length === 0) {
    searchList.push({ key: null, operator: null, value: undefined })
  }
}

watch(() => localList.value, filterSearchList, { deep: true })

watch(
  () => props.operatorSelectable,
  () => {
    searchList.length = 0
    searchList.push({ key: null, operator: null, value: undefined })
  },
)

defineExpose({ getValue, clearValue, setValue, handleConfig })

onMounted(() => {
  nameOpen.value = false
})
// 组件卸载时执行
onUnmounted(() => {
  if (props.localKey && localScheme.value.length === 0) {
    localStorage.removeItem(props.localKey)
  }
})
</script>
